import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import './NotFound.css'

const TOTAL_DURATION = 3000 // 3 seconds total

const NotFound = () => {
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(3)
  const [isFadingNumber, setIsFadingNumber] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [progress, setProgress] = useState(0)
  const startTimeRef = useRef(performance.now())
  const rafRef = useRef<number>(0)

  // Smooth continuous progress animation
  useEffect(() => {
    const animate = (now: number) => {
      const elapsed = now - startTimeRef.current
      const p = Math.min(elapsed / TOTAL_DURATION, 1)
      setProgress(p)

      if (p < 1) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }
    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Countdown number changes at 1s intervals
  useEffect(() => {
    const timer = setInterval(() => {
      setIsFadingNumber(true)
      setTimeout(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            setIsFadingOut(true)
            setTimeout(() => navigate('/'), 800)
            return 0
          }
          return prev - 1
        })
        setIsFadingNumber(false)
      }, 300)
    }, 1000)

    return () => clearInterval(timer)
  }, [navigate])

  const handleGoHome = () => {
    setIsFadingOut(true)
    setTimeout(() => navigate('/'), 800)
  }

  // SVG circle parameters
  const radius = 60
  const circumference = 2 * Math.PI * radius
  const strokeOffset = circumference * (1 - progress)

  return (
    <div className={`not-found-page ${isFadingOut ? 'fade-out' : ''}`}>
      <div className="not-found-container">
        <div className="not-found-content">
          <div className="not-found-loader">
            <svg className="countdown-circle" viewBox="0 0 140 140">
              <circle
                className="countdown-track"
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                strokeWidth="2"
              />
              <circle
                className="countdown-progress"
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                strokeWidth="2"
                strokeDasharray={circumference}
                strokeDashoffset={strokeOffset}
                strokeLinecap="round"
              />
            </svg>
            <span className={`countdown-number ${isFadingNumber ? 'fading' : ''}`}>
              {countdown}
            </span>
          </div>
          <h1 className="not-found-title">page not found</h1>
          <p className="not-found-description">
            the signal you're looking for doesn't exist in this dimension
          </p>
          <p className="not-found-redirect-text">redirecting home</p>
          <button className="not-found-button" onClick={handleGoHome}>
            return home
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotFound
